import { Component, DestroyRef, inject, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { CommonService } from 'src/app/shared/services/common.service';
import { InteractionType } from '@azure/msal-browser';
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { Router } from '@angular/router';
import {
  pageUrl,
  StatusCodes,
  USER_PROFILE_ALL_FACILITIES_LABEL,
} from 'src/app/shared/utility/constants';
import { isSingleWarehouseAllFacilitiesEquivalent, isAllFacilitiesProfileOptionAvailable } from 'src/app/shared/utility/user-profile-all-facilities.util';
import { isRolesPermissionsEnforcementEnabled } from 'src/app/core/config/roles-permissions-enforcement.config';
import { UserService } from 'src/app/features/service/user.service';
import { RpeRuntimeBridgeService } from 'src/app/shared/services/rpe-runtime-bridge.service';
import { UserProfileInfo } from 'src/app/features/components/user-profile/user-profile.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { INotificationData, INotificationResponse } from './header.model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  @ViewChild('notificationMenuTrigger') notificationMenuTrigger!: MatMenuTrigger;

  private readonly destroyRef = inject(DestroyRef);
  private readonly rpeRuntimeBridge = inject(RpeRuntimeBridgeService);

  public isOverlayOpened!: boolean;
  public userFullName!: string;
  public userFirstName!: string;
  public userIntial!: string;
  public userLocation!: string;
  public notificationData: INotificationData[] = [];
  public unreadNotificationCount = 0;
  public isLoadingNotifications = false;

  constructor(
    private readonly commonService: CommonService,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {
    this.commonService.checkOverlayOpened
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.isOverlayOpened = data;
      });
    this.userService.getUserProfileFromStorage();
    this.getUserDetails();
  }

  public logout() {
    const activeAccount =
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
    this.rpeRuntimeBridge.clearEffectivePermissions();
    this.localStorageService.clear();
    sessionStorage.clear();
  }

  public redirectToUserProfile() {
    this.router.navigate([pageUrl.userProfile]);
  }

  private organizeHeaderDetails(data: UserProfileInfo) {
    this.userFirstName = data.firstName;
    this.userFullName = `${data.firstName}  ${data.lastName}`;
    const firstInitial = data.firstName?.charAt(0).toUpperCase() || '';
    const lastInitial = data.lastName?.charAt(0).toUpperCase() || '';
    this.userIntial = firstInitial + lastInitial;
    const showAllLabel =
      isAllFacilitiesProfileOptionAvailable(data, {
        enforcementEnabled: isRolesPermissionsEnforcementEnabled(),
        isSuperAdmin: data.isSuperAdmin === true,
      }) &&
      data.isAllWarehouse === true &&
      !isSingleWarehouseAllFacilitiesEquivalent(data);
    this.userLocation = showAllLabel
      ? USER_PROFILE_ALL_FACILITIES_LABEL
      : data.defaultLocation?.name ?? '';
  }

  private getUserDetails() {
    this.userService.userProfileInfo
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.organizeHeaderDetails(data);
        }
      });
  }

  public onNotificationBellClick(event: Event): void {
    if (this.notificationMenuTrigger?.menuOpen) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.notificationMenuTrigger) {
      this.notificationMenuTrigger.closeMenu();
    }
    this.fetchAlertNotifications();
  }

  public markAllAsRead(event: Event): void {
    event.stopPropagation();
    const unreadIds = this.notificationData
      .filter((item) => !item.isSeen)
      .map((item) => item.id);

    if (unreadIds.length === 0) {
      return;
    }
    this.userService
      .markNotificationsAsRead(unreadIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.statusCode === StatusCodes.Success) {
            this.notificationData = this.notificationData.map((item) => ({
              ...item,
              isSeen: true,
            }));
            this.unreadNotificationCount = 0;
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private fetchAlertNotifications(): void {
    this.isLoadingNotifications = true;
    this.userService
      .fetchAlertNotifications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: INotificationResponse) => {
          this.isLoadingNotifications = false;
          if (response.statusCode === StatusCodes.Success) {
            this.notificationData = response.data || [];
            this.unreadNotificationCount = this.notificationData.filter(
              (item) => !item.isSeen
            ).length;
            if (this.notificationMenuTrigger) {
              this.notificationMenuTrigger.openMenu();
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isLoadingNotifications = false;
          this.errorHandlerService.handleError(error);
        },
      });
  }

  public onNotificationMenuClosed(): void {
    this.notificationData = [];
    this.isLoadingNotifications = false;
  }  
}

